<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import QRCode from "qrcode";
import Button from "../components/Button.vue";
import { api } from "../api";
import { useAuth } from "../composables/useAuth";

const emit = defineEmits<{ signedIn: [] }>();
const auth = useAuth();
const step = ref<"code" | "totp">("code");
const code = ref("");
const totp = ref("");
const challenge = ref("");
const error = ref("");
const submitting = ref(false);
const username = ref("");
const codeLogin = ref(true);
const qr = ref("");

async function loadBot() {
  try {
    const bot = await api.get<{ username?: string; codeLogin?: boolean }>("/auth/bot");
    username.value = bot.username || "";
    codeLogin.value = bot.codeLogin !== false;
  } catch {
    codeLogin.value = false;
  }
}

watch(username, async (name) => {
  qr.value = name ? await QRCode.toString(`https://t.me/${name}`, { type: "svg", margin: 1, width: 160 }) : "";
}, { immediate: true });

async function submitCode() {
  if (submitting.value || code.value.length !== 6) return;
  submitting.value = true;
  error.value = "";
  try {
    const result = await auth.loginWithCode(code.value);
    if (result.totpRequired && result.challenge) {
      step.value = "totp";
      challenge.value = result.challenge;
      totp.value = "";
      return;
    }
    emit("signedIn");
  } catch (e) {
    error.value = e instanceof Error ? e.message : "登录失败";
  } finally {
    submitting.value = false;
  }
}

async function submitTotp() {
  if (submitting.value || totp.value.length !== 6) return;
  submitting.value = true;
  error.value = "";
  try {
    await auth.confirmTotp(challenge.value, totp.value);
    emit("signedIn");
  } catch (e) {
    error.value = e instanceof Error ? e.message : "验证失败";
  } finally {
    submitting.value = false;
  }
}

function backToCode() {
  step.value = "code";
  totp.value = "";
  error.value = "";
}

function digits(value: string) {
  return value.replace(/\D/g, "").slice(0, 6);
}

onMounted(loadBot);
</script>

<template>
  <div class="auth-body">
    <aside class="auth-brand">
      <div class="auth-brand-top">
        <span class="auth-logo">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h13M8 12h13M8 18h13"/><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"/></svg>
        </span>
        <div>
          <div class="brand-name">RiskOps 风控中心</div>
          <div class="brand-sub">Risk Operations</div>
        </div>
      </div>
      <div class="auth-brand-mid">
        <h2>高效协作，<br>风险尽在掌握</h2>
        <p>通过 Telegram 登录码进入风控中心，处理任务、风险事件与复核。</p>
        <ul class="auth-features">
          <li><span class="fi"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="7"/><rect x="12" y="7" width="3" height="11"/><rect x="17" y="13" width="3" height="5"/></svg></span>任务闭环：创建 · 指派 · 移交 · 完成</li>
          <li><span class="fi"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg></span>风险事件、日志与附件</li>
          <li><span class="fi"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/></svg></span>审批与值班协同</li>
        </ul>
      </div>
      <div class="auth-brand-foot">© 2026 RiskOps · 内部风控协作平台</div>
    </aside>
    <main class="auth-main">
      <div class="auth-card">
        <div class="auth-mobile-brand">
          <span class="auth-logo"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 6h13M8 12h13M8 18h13"/><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"/></svg></span>
          <div class="mn">RiskOps 风控中心</div>
        </div>
        <template v-if="step === 'code'">
          <h1>欢迎回来</h1>
          <p class="sub">使用 Telegram 账号登录</p>
          <div v-if="error" class="auth-alert">{{ error }}</div>
          <div class="code-login">
            <div v-if="qr" class="tg-qr"><div class="qr-box" v-html="qr"></div></div>
            <ol class="code-steps">
              <li>在 Telegram 私聊机器人 <b>{{ username ? `@${username}` : "（扫上方二维码）" }}</b></li>
              <li>发送 <b>/login</b> 获取 6 位登录码</li>
              <li>在下方输入登录码</li>
            </ol>
            <div v-if="!codeLogin" class="auth-alert">取码机器人当前不可用，请联系管理员</div>
            <form class="code-form" @submit.prevent="submitCode">
              <input v-model="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6 位登录码" aria-label="登录码" required @input="code = digits(code)">
              <Button variant="primary" type="submit" :disabled="submitting || code.length !== 6">登录</Button>
            </form>
          </div>
        </template>
        <template v-else>
          <h1>两步验证</h1>
          <p class="sub">输入验证器 App 里的 6 位动态码</p>
          <div v-if="error" class="auth-alert">{{ error }}</div>
          <form class="code-form" @submit.prevent="submitTotp">
            <input v-model="totp" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6 位动态码" aria-label="两步验证码" required @input="totp = digits(totp)">
            <Button variant="primary" type="submit" :disabled="submitting || totp.length !== 6">验证并登录</Button>
          </form>
          <button class="back" type="button" @click="backToCode">返回，重新获取登录码</button>
        </template>
      </div>
    </main>
  </div>
</template>

<style>
.auth-body { min-height: 100vh; display: flex; background: var(--ro-bg); color: var(--ro-text); }
.auth-brand { flex: 0 0 46%; position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; padding: 52px 56px; color: #e8ebf2; background: radial-gradient(700px 420px at 0% 0%, rgba(109,124,255,.22), transparent 60%), radial-gradient(620px 520px at 100% 100%, rgba(139,92,246,.2), transparent 55%), linear-gradient(160deg, #1a2038 0%, #13182c 55%, #0d1120 100%); }
.auth-brand::after { content: ""; position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px); background-size: 40px 40px; mask-image: radial-gradient(ellipse at 18% 8%, #000 0%, transparent 72%); pointer-events: none; }
.auth-brand-top, .auth-brand-mid, .auth-brand-foot { position: relative; z-index: 1; }
.auth-brand-top { display: flex; align-items: center; gap: 12px; }
.auth-logo { width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; color: #fff; background: linear-gradient(140deg, #6d7cff, #8b5cf6); box-shadow: 0 6px 20px rgba(109,124,255,.35); }
.auth-brand .brand-name { font-weight: 700; font-size: 16px; }
.auth-brand .brand-sub { font-size: 12px; color: rgba(232,235,242,.55); }
.auth-brand-mid { max-width: 380px; }
.auth-brand-mid h2 { font-size: 30px; font-weight: 700; line-height: 1.32; margin: 0 0 14px; }
.auth-brand-mid p { color: rgba(232,235,242,.72); font-size: 14.5px; margin: 0 0 28px; line-height: 1.7; }
.auth-features { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.auth-features li { display: flex; align-items: center; gap: 12px; font-size: 14px; }
.auth-features .fi { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; color: #aab4ff; background: rgba(109,124,255,.18); }
.auth-brand-foot { font-size: 12px; color: rgba(232,235,242,.4); }
.auth-main { flex: 1; display: flex; align-items: center; justify-content: center; padding: 40px 28px; }
.auth-card { width: 100%; max-width: 380px; }
.auth-card h1 { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.auth-card .sub { color: var(--ro-muted); font-size: 13.5px; margin: 0 0 26px; }
.auth-mobile-brand { display: none; text-align: center; margin-bottom: 28px; }
.auth-mobile-brand .auth-logo { margin: 0 auto 12px; }
.auth-mobile-brand .mn { font-weight: 700; font-size: 19px; }
.auth-alert { margin: 0 0 16px; padding: 10px 12px; border-radius: 8px; color: var(--ro-danger); background: var(--ro-danger-soft); }
.code-steps { list-style: none; margin: 0 0 18px; padding: 0; counter-reset: s; }
.code-steps li { counter-increment: s; position: relative; padding: 7px 0 7px 34px; color: var(--ro-muted); font-size: 13.5px; }
.code-steps li::before { content: counter(s); position: absolute; left: 0; top: 7px; width: 22px; height: 22px; border-radius: 50%; background: rgba(109,124,255,.16); color: #6d7cff; font-weight: 600; font-size: 12px; display: grid; place-items: center; }
.code-steps b { color: var(--ro-text); }
.code-form { display: flex; gap: 10px; }
.code-form input { flex: 1; text-align: center; letter-spacing: 5px; font-size: 18px; font-weight: 600; padding: 12px; }
.tg-qr { display: flex; justify-content: center; margin: 6px 0 16px; }
.qr-box { background: #fff; padding: 10px; border-radius: 12px; line-height: 0; }
.qr-box :deep(svg), .qr-box svg { width: 160px; height: 160px; }
.back { margin-top: 14px; border: 0; background: transparent; color: var(--ro-muted); cursor: pointer; padding: 0; }
@media (max-width: 900px) {
  .auth-brand { display: none; }
  .auth-mobile-brand { display: block; }
  .code-form { flex-direction: column; }
}
</style>
